import { LoadingOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { useState } from 'react'
import { RiFileExcel2Line } from 'react-icons/ri'

import { restClient } from '@/Core'
import { cx } from '@/Ship/Helpers/cssClass'
import { notify } from '@/Ship/Helpers/notify'
import listStyles from '@/Ship/Templates/CrudResource/CrudResourceTemplate.module.css'

import { contentDispositionFileName, saveBlob } from '../Helpers/reportDownload'

interface ReportExportButtonProps {
    /** Ендпоінт файлу звіту */
    url: string
    /** Ті самі фільтри, розріз і сортування, що на екрані */
    params: Record<string, string>
    /** Ім'я файлу, якщо бекенд його не передав */
    fallbackFileName: string
}

/** Великий звіт формується довше за типові 30 с restClient */
const EXPORT_TIMEOUT_MS = 120_000

const errorMessage = (e: any): string => {
    if (e?.name === 'TimeoutError') return 'Файл формується надто довго — звузьте період чи фільтри'
    const status = e?.statusCode ?? e?.response?.status
    if (status === 403) return 'Немає доступу до звітів'
    if (status === 400) return 'Некоректні параметри звіту'
    return 'Не вдалося сформувати файл. Спробуйте ще раз.'
}

/**
 * Кнопка «Експорт в Excel» шапки звіту (квадрат 36×36, як фільтр і «Оновити»): файл формує
 * бекенд за поточними фільтрами, ім'я — з Content-Disposition. Під час формування — спінер,
 * повторний клік ігнорується (не disabled — див. RefreshButton про підказку antd).
 */
export const ReportExportButton = ({ url, params, fallbackFileName }: ReportExportButtonProps) => {
    const [exporting, setExporting] = useState(false)
    const label = 'Експорт в Excel'

    const exportFile = async () => {
        if (exporting) return
        setExporting(true)
        try {
            const response = await restClient.get(
                url,
                { searchParams: new URLSearchParams(params), timeout: EXPORT_TIMEOUT_MS },
                // Без трансформера: глобальний (ServiceConfig) читає тіло як JSON, а тут — xlsx
                { isDisableResponseCache: true, isOriginalResponse: true, disableResponseTransform: true }
            )
            if (!response) throw new Error('Empty response')
            const blob = await response.blob()
            saveBlob(blob, contentDispositionFileName(response.headers.get('Content-Disposition')) ?? fallbackFileName)
        } catch (e) {
            notify.error(errorMessage(e))
        } finally {
            setExporting(false)
        }
    }

    return (
        <Tooltip title={label}>
            <button
                type="button"
                className={cx(listStyles, 'iconButton')}
                aria-label={label}
                aria-busy={exporting}
                onClick={exportFile}
            >
                {exporting ? <LoadingOutlined /> : <RiFileExcel2Line />}
            </button>
        </Tooltip>
    )
}
